<template>
    <component
        :is="componentType"
        v-bind="componentProps"
        v-model="show"
    >
        <v-card
            flat
            :max-height="fullscreen ? '100vh' : size * 1.5"
            :min-height="fullscreen ? '100vh' : null"
            :light="lightTheme"
            :class="[{ 'mobile-scroll': $vuetify.breakpoint.xs && mobileScroll }]"
            class="soli-responsive-dialog half-round safe-bottom"
            style="overflow: auto"
        >
            <v-container
                :class="{
                    'pt-0': !!title || !!$slots.title || $vuetify.breakpoint.xs,
                    'pa-0': hideContainer,
                }"
            >
                <div>
                    <v-btn
                        icon
                        v-if="close"
                        @click="show = false"
                        class="ma-3 soli-close"
                        ><v-icon>clear</v-icon></v-btn
                    >
                    <v-card-title
                        v-if="title || $slots.title"
                        :class="[
                            ...titleClasses,
                            {
                                'text-sm-subtitle-1': menuProps.isMenu,
                            },
                        ]"
                        ><slot name="title"></slot>{{ title }}</v-card-title
                    >
                    <v-card-subtitle
                        v-if="subtitle || $slots.subtitle"
                        :class="subtitleClasses"
                        ><slot name="subtitle"></slot>{{ subtitle }}</v-card-subtitle
                    >
                </div>

                <slot name="body"></slot>
            </v-container>
            <slot name="cover"></slot>
        </v-card>
    </component>
</template>

<script>
import { VDialog, VBottomSheet, VMenu } from 'vuetify/lib';

export default {
    components: {
        VDialog,
        VBottomSheet,
        VMenu,
    },
    props: {
        light: {
            type: Boolean,
            default: null,
        },
        large: {
            type: Boolean,
            default: false,
        },
        size: {
            type: [String, Number],
            default: 450,
        },
        menuProps: {
            type: Object,
            default: () => ({
                isMenu: false,
                width: 250,
                element: '.soli-resp-menu',
            }),
        },
        fullscreen: {
            type: Boolean,
            default: false,
        },
        close: {
            type: Boolean,
            default: true,
        },
        title: {
            type: String,
            default: null,
        },
        titleClasses: {
            type: Array,
            default: () => [],
        },
        subtitle: {
            type: String,
            default: null,
        },
        subtitleClasses: {
            type: Array,
            default: () => [],
        },
        bottomSheetOnly: {
            type: Boolean,
            default: false,
        },
        mobileScroll: {
            type: Boolean,
            default: true,
        },
        hideContainer: {
            type: Boolean,
            default: false,
        },
        persistent: {
            type: Boolean,
            default: false,
        },
        color: {
            type: String,
            default: 'white',
        },
        right: {
            type: Boolean,
            default: false,
        },
        closeOnContentClick: {
            type: Boolean,
            default: true,
        },
        rounded: {
            type: Boolean,
            default: false,
        },
    },
    data() {
        return {
            show: true,
        };
    },
    computed: {
        lightTheme() {
            if (this.light) return this.light;
            return !this.$vuetify.theme.isDark;
        },
        isMobile() {
            return this.$vuetify.breakpoint.xs || this.bottomSheetOnly;
        },
        componentType() {
            if (this.isMobile) return 'v-bottom-sheet';
            if (this.menuProps.isMenu) return 'v-menu';
            return 'v-dialog';
        },
        componentProps() {
            if (this.isMobile) {
                return {
                    persistent: this.persistent,
                    'close-on-content-click': this.closeOnContentClick && !this.persistent,
                    class: 'soli-round mx-1',
                    'content-class': 'half-round overflow-hidden safe-bottom white flex flex-column surface',
                };
            }

            if (this.menuProps.isMenu) {
                return {
                    'max-width': this.menuProps.width,
                    'min-width': this.menuProps.width,
                    activator: this.menuProps.element,
                    'offset-y': true,
                    'nudge-bottom': this.menuProps.nudgeBottom || 0,
                    'nudge-left': this.menuProps.nudgeLeft || 0,
                    left: !this.right,
                    right: this.right,
                    'content-class': 'soli-round',
                    'close-on-content-click': this.closeOnContentClick && !this.persistent,
                };
            }

            return {
                'max-width': this.fullscreen ? null : this.size,
                'content-class': this.rounded ? 'full-round' : 'soli-round',
                transition: this.$vuetify.breakpoint.smAndDown ? 'dialog-bottom-transition' : 'dialog-transition',
                persistent: this.persistent,
            };
        },
    },
    watch: {
        show(val) {
            if (!val) {
                this.$emit('closing');
                this.closeModal(() => this.$emit('reset'));
            }
        },
    },
};
</script>

<style scoped>
.v-dialog__content--active {
    backdrop-filter: blur(1px);
}
</style>
